// Base component (not a leaf theme's override): the dialer's JS lives at the base
// tier so every leaf theme inherits it unchanged — metronic_8 reaches it through
// hiveMind.newComponent()'s base fallback, cleanSlate through its base import.meta.glob.
const component = require('../component/component');

// Numeric quantity stepper. The root <div data-zit-component="dialer"> frames a
// number <input> (the submittable control, carrying the array name) between a
// decrement and an increment button. Pure DOM behaviour, no CSS-framework
// dependency:
//   - the buttons step the value by `step`, clamped to [min, max];
//   - typing into the input is re-clamped on change;
//   - each button/blur reflects at the two buttons' disabled state (min/max reached);
//   - every value change dispatches a bubbling `zitDialerChange` CustomEvent
//     (detail: {id, value, max}) so a coordinator — e.g. the optional-products
//     carousel enforcing a global remaining cap — can react without reaching in.
// min/max/step arrive as data-zit-min / -max / -step (readZAttributes maps them
// onto the matching properties below).
const dialer = Object.assign({}, component, {
    subelements: {
        input: null,
        decrementButton: null,
        incrementButton: null,
    },
    min: 0,
    max: 1,
    step: 1,

    init: function () {
        component.init.call(this);

        if (!this.subelements.input) {
            return;
        }

        this.wire();
        // Reflect the initial (server-clamped) value at the buttons.
        this.reflect();
    },

    wire: function () {
        if (this.subelements.decrementButton) {
            this.subelements.decrementButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.stepBy(-this.step);
            });
        }
        if (this.subelements.incrementButton) {
            this.subelements.incrementButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.stepBy(this.step);
            });
        }
        // Re-clamp a hand-typed value. clamp() never re-fires a native change, so
        // this listener cannot recurse.
        this.subelements.input.addEventListener('change', () => { this.clamp(); });
    },

    current: function () {
        let value = parseInt(this.subelements.input.value, 10);
        return isNaN(value) ? this.min : value;
    },

    clampValue: function (value) {
        if (value < this.min) {
            return this.min;
        }
        if (value > this.max) {
            return this.max;
        }
        return value;
    },

    // Write a clamped value to the input and announce the change. `fireNative`
    // fires a native `change` on the input as well (for button-driven steps, so
    // form/validation observers see it) — never true when called from the input's
    // own change handler, so there is no feedback loop.
    write: function (value, fireNative) {
        value = this.clampValue(value);
        this.subelements.input.value = value;
        this.reflect();

        this.element.dispatchEvent(new CustomEvent('zitDialerChange', {
            bubbles: true,
            detail: { id: this.id, value: value, max: this.max }
        }));

        if (fireNative) {
            this.subelements.input.dispatchEvent(new Event('change'));
        }
    },

    stepBy: function (delta) {
        this.write(this.current() + delta, true);
    },

    // Re-clamp an out-of-range typed value (no native change re-fire).
    clamp: function () {
        this.write(this.current(), false);
    },

    // Disable each button when its direction is exhausted, and flag whether a positive
    // quantity is selected so a host can reflect it (e.g. cleanSlate's product card lifts
    // itself via :has([data-zit-dialer-active="true"]), mirroring the mock's .pcard.sel).
    reflect: function () {
        let value = this.current();
        if (this.subelements.decrementButton) {
            this.subelements.decrementButton.disabled = (value <= this.min);
        }
        if (this.subelements.incrementButton) {
            this.subelements.incrementButton.disabled = (value >= this.max);
        }
        this.element.dataset.zitDialerActive = (value > this.min) ? 'true' : 'false';
    },

    // Coordinator hook: lower or raise the ceiling at runtime (e.g. a global
    // remaining cap), re-clamping the current value and refreshing the buttons.
    setMax: function (max) {
        this.max = max;
        this.write(this.current(), false);
    },

    // Coordinator hook: set the value programmatically (clamped to [min,max]),
    // refreshing the buttons. Used by the bundleOptionalsGatekeeper to pull a
    // hand-typed over-limit value back down.
    setCount: function (value) {
        this.write(value, false);
    },

    // The dialer's value is the inner input's, not the root div's.
    getElementValue: function () {
        return this.subelements.input ? this.subelements.input.value : this.element.value;
    },
});

module.exports = dialer;
